import { ColumnDef } from '@if/mui'
import { useMonolithBaseUrl } from '@src/services/monolith-hook'
import { InteracWire } from '@src/types/interac'
import { Calendar } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

/** `DateTime.MinValue` as the monolith serializes it — a transfer with no reception date. */
const NO_RECEIVED_ON = '0001-01-01T00:00:00'

export const useRecordedInteracWireColumns = (): ColumnDef<InteracWire>[] => {
  const { t } = useTranslation()
  const monolithBaseUrl = useMonolithBaseUrl()

  return useMemo<ColumnDef<InteracWire>[]>(
    () => [
      {
        name: 'receivedOn',
        type: 'dateonly',
        header: t('common.date'),
        getValue: (row) => (row.receivedOn === NO_RECEIVED_ON ? null : row.receivedOn),
        minWidth: 130,
      },
      {
        name: 'amount',
        type: 'currencydecimal',
        header: t('interacWire.amount'),
        width: 130,
      },
      {
        name: 'sentBy',
        type: 'titleSubtitle',
        header: t('interacWire.from'),
        subtitle: 'senderEmail',
        minWidth: 220,
        flexGrow: 1,
      },
      {
        name: 'message',
        header: t('interacWire.message'),
        minWidth: 200,
        flexGrow: 1,
      },
      {
        name: 'linkedBy',
        header: t('interacHistory.linkedBy'),
        minWidth: 150,
      },
      {
        name: 'linkedOn',
        type: 'dateonly',
        header: t('interacHistory.linkedOn'),
        minWidth: 130,
      },
      {
        name: 'linkedLoan',
        header: t('interacHistory.loanInfo'),
        getValue: (row) => (row.linkedLoanId ? `${row.linkedProgram ?? ''} #${row.linkedLoanId}` : null),
        minWidth: 150,
      },
      {
        name: 'clientName',
        header: t('interacHistory.clientName'),
        getValue: (row) => {
          const match = row.possibleMatches?.find((possibleMatch) => possibleMatch.loanId === row.linkedLoanId)
          if (!row.linkedLoanId || !match) return null
          return `${match.applicantFirstName} ${match.applicantLastName}`
        },
        emptyPlaceholder: '—',
        minWidth: 180,
      },
      {
        name: 'calendar',
        type: 'action',
        actions: [
          {
            icon: Calendar,
            label: t('interacHistory.paymentCalendar'),
            hidden: (row) => !row.linkedLoanId,
            href: (row) => `${monolithBaseUrl}/admin/customer/${row.linkedLoanId}`,
            target: '_blank',
          },
        ],
      },
    ],
    [monolithBaseUrl, t],
  )
}
